import {useMutation, useQueryClient} from 'react-query';

import {
    ChangeRequest, SearchResponse, UpdateSchoolAcademicYearResponse
} from '../../api/types';
import {useAppContext} from '../app-context/use-app-context';
import {
    resolveResourceResponseErrorMessage, resolveResponseData
} from '../../api/utils/resolve-response-data';
import {SCHOOL_ACADEMIC_YEARS_SEARCH_KEY} from '../queries/use-query-school-academic-years-search';
import {useSnackbar} from '../../components/snackbars/snackbars';

export const useMutationSetSchoolAcademicYear = () => {
    const {api: {schoolAcademicYearsPut}} = useAppContext();
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();

    return useMutation<UpdateSchoolAcademicYearResponse, undefined, ChangeRequest>(
        async input => {
            const response = await schoolAcademicYearsPut(input);
            return resolveResponseData<UpdateSchoolAcademicYearResponse>(response);
        },
        {
            onSuccess: async (data: UpdateSchoolAcademicYearResponse) => {
                // manually update the cache, calling invalidateQueries will not work
                // as the search api response is not invalidated in the backend adequately
                queryClient.setQueriesData<SearchResponse>({
                    queryKey: SCHOOL_ACADEMIC_YEARS_SEARCH_KEY,
                    exact: false
                }, (cacheData = {schools: [], totalSchoolsFound: 0, totalResultPages: 0}): SearchResponse => {
                    if (!cacheData) {
                        return cacheData;
                    }
                    const schoolsCache = cacheData?.schools || [];

                    const schools = schoolsCache.map(schoolInCache => {
                        // a "Region" academic id will change when a successful PUT is completed with a new id
                        // so to know which school is updated from the search response, use the school id if available
                        if (schoolInCache?.academicYear && schoolInCache.schoolId === data?.schoolId) {
                            schoolInCache.academicYear.endDate = data.endDate;
                            schoolInCache.academicYear.isSchoolAcademicYear = true;
                            // Update with the new school academic year id
                            if (data.id) {
                                schoolInCache.academicYear.academicYearId = data.id;
                            }
                        }

                        return schoolInCache;
                    });

                    return {...cacheData, schools};
                });
            },
            onError: (error) => {
                const message = resolveResourceResponseErrorMessage(error);
                enqueueSnackbar(message, {variant: 'error'});
            }
        }
    );
};
